import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useUploadActivity } from "@/data/activities/hooks";
import { SportType } from "@/data/activities/types";
import { useCurrentUser } from "@/data/auth/hooks";
import dayjs from "@/lib/dayjs";
import { sportConfigs } from "@/lib/sport-utils";
import { initiateLinkWithStrava } from "@/lib/strava-utils";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import {
  CalendarIcon,
  Check,
  ChevronRight,
  Clock,
  Ruler,
  Upload,
} from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";

type Step = "sport" | "details";

export function UploadActivityDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [step, setStep] = useState<Step>("sport");
  const [selectedSport, setSelectedSport] = useState<SportType | null>(null);
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [duration, setDuration] = useState("");
  const [distance, setDistance] = useState("");
  const [elevation, setElevation] = useState("");

  const [dateModalOpen, setDateModalOpen] = useState(false);

  // Get current user data to check Strava connection status
  const { data: user } = useCurrentUser();

  const today = dayjs().endOf("day").toDate();
  const oneWeekAgo = dayjs().subtract(1, "week").startOf("day").toDate();

  const uploadActivity = useUploadActivity();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setStep("sport");
      setSelectedSport(null);
      setDate(new Date());
      setDuration("");
      setDistance("");
      setElevation("");
    }
  }, [open]);

  // Reset mutation state when dialog closes
  useEffect(() => {
    if (!open) {
      uploadActivity.reset();
    }
  }, [open, uploadActivity]);

  const handleSportSelect = (sport: SportType) => {
    setSelectedSport(sport);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!selectedSport || !duration || !date) return;

    const selectedSportConfig = selectedSport
      ? sportConfigs.find((s) => s.id === selectedSport)
      : null;

    const needsDistance = selectedSportConfig?.requiresDistance ?? false;

    if (needsDistance && !distance) {
      toast.error("Please provide a distance");
      return;
    }

    const activityData = {
      type: selectedSport,
      duration: Number(duration) * 60,
      date: date.toISOString(),
      ...(distance ? { distance: Number(distance) } : {}),
      ...(elevation ? { elevation: Number(elevation) } : {}),
    };

    uploadActivity.mutate(activityData, {
      onSuccess: () => {
        toast.success("Activity uploaded successfully");
        onOpenChange(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to upload activity");
      },
    });
  };

  const handleStravaLink = () => {
    // Initiate Strava OAuth flow
    initiateLinkWithStrava();
  };

  const selectedSportConfig = selectedSport
    ? sportConfigs.find((s) => s.id === selectedSport)
    : null;

  const needsDistance = selectedSportConfig?.requiresDistance ?? false;
  const needsElevation = selectedSportConfig?.requiresElevation ?? false;

  // Format minutes to hours and minutes
  const formatDuration = (minutes: string) => {
    if (!minutes) return "";
    const mins = Number.parseInt(minutes, 10);
    if (isNaN(mins)) return "";

    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;

    if (hours > 0) {
      return `${hours}h ${remainingMins}m`;
    }
    return `${remainingMins}m`;
  };

  const formattedDuration = formatDuration(duration);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md mx-auto bg-gradient-to-br from-white/90 to-white/80 backdrop-blur-lg border border-white/20 shadow-xl">
        <div className="absolute -z-10 inset-0 bg-gradient-to-br from-[#2b7fff]/15 to-[#fe9a00]/15 rounded-lg"></div>

        <DialogHeader>
          <DialogTitle className="flex items-center text-xl">
            <Upload className="mr-2 h-6 w-6 text-blue-500" />
            Upload Activity
          </DialogTitle>
          <DialogDescription className="text-base text-gray-700">
            {step === "sport"
              ? "Select the type of activity you performed"
              : `Add details for your ${selectedSport} activity`}
          </DialogDescription>
        </DialogHeader>

        <div className={`py-4 ${step === "sport" ? "" : "hidden"}`}>
          <div className="rounded-lg border border-white/30 bg-white/50 backdrop-blur-sm p-4 mx-0 shadow-sm">
            <h3 className="text-sm font-medium mb-3">Select Activity Type</h3>
            <ScrollArea className="h-[300px] pr-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 px-1 py-1">
                {sportConfigs
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((sport) => (
                    <Card
                      key={sport.id}
                      className={cn(
                        "py-1",
                        "cursor-pointer hover:bg-white/70 transition-colors",
                        selectedSport === sport.id
                          ? "bg-white/70 ring-1 ring-blue-600 border border-blue-300"
                          : "bg-white/50"
                      )}
                      onClick={() => handleSportSelect(sport.id)}
                    >
                      <CardContent className="p-3 py-0">
                        <div className="flex md:flex-col md:items-center items-center">
                          <div className="text-2xl mr-3 md:mr-0 md:mb-1">
                            {sport.emoji}
                          </div>
                          <div className="text-sm font-medium md:text-center">
                            {sport.name}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </ScrollArea>
          </div>

          <div className="rounded-lg border border-white/30 bg-white/50 backdrop-blur-sm p-4 mt-4 mx-0 shadow-sm hidden md:block">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg viewBox="0 0 24 24" className="h-8 w-8" fill="#FC4C02">
                    <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium">Connect with Strava</h4>
                  <p className="text-xs text-muted-foreground">
                    {user?.stravaId
                      ? "Your Strava account is connected. Future activities will be uploaded automatically."
                      : "Link your Strava account to automatically upload future activities."}
                  </p>
                </div>
              </div>
              {user?.stravaId ? (
                <div className="flex items-center text-xs text-green-600 font-medium">
                  <Check className="mr-1 h-3 w-3" />
                  Connected
                </div>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  className="bg-[#FC4C02] hover:bg-[#E34402] text-xs h-7 px-2"
                  onClick={handleStravaLink}
                >
                  Connect
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className={`py-4 ${step === "details" ? "" : "hidden"}`}>
          <div className="rounded-lg border border-white/30 bg-white/50 backdrop-blur-sm p-4 mx-0 shadow-sm space-y-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Popover
                modal
                onOpenChange={setDateModalOpen}
                open={dateModalOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal bg-white/80",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    weekStartsOn={1}
                    onSelect={(date) => {
                      setDateModalOpen(false);
                      setDate(
                        dayjs(date)
                          .set("hour", new Date().getHours())
                          .set("minute", new Date().getMinutes())
                          .toDate()
                      );
                    }}
                    autoFocus
                    disabled={(date) => {
                      return date > today || date < oneWeekAgo;
                    }}
                    footer={
                      <p className="text-xs text-center text-muted-foreground p-2">
                        Activities must be from within last week
                      </p>
                    }
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">Duration (minutes)</Label>
              <div className="relative">
                <Input
                  id="duration"
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g. 45"
                  className="pr-20 bg-white/80"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500">
                  <Clock className="mr-1 h-4 w-4" />
                  <span className="text-sm">{formattedDuration}</span>
                </div>
              </div>
            </div>

            {needsDistance && (
              <div className="space-y-2">
                <Label htmlFor="distance">Distance (km)</Label>
                <div className="relative">
                  <Input
                    id="distance"
                    type="number"
                    step="0.01"
                    value={distance}
                    required={true}
                    onChange={(e) => setDistance(e.target.value)}
                    placeholder="e.g. 5.2"
                    className={cn(
                      "pr-16 bg-white/80",
                      !distance && "border-red-200 ring-1 ring-red-200"
                    )}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500">
                    <Ruler className="mr-1 h-4 w-4" />
                    <span className="text-sm">km</span>
                  </div>
                </div>
              </div>
            )}

            {needsElevation && (
              <div className="space-y-2">
                <Label htmlFor="elevation">Elevation (m)</Label>
                <div className="relative">
                  <Input
                    id="elevation"
                    type="number"
                    step="1"
                    value={elevation}
                    onChange={(e) => setElevation(e.target.value)}
                    placeholder="e.g. 100"
                    className="pr-8 bg-white/80"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500">
                    <span className="text-sm">m</span>
                  </div>
                </div>
              </div>
            )}

            {/* Strava integration section */}
            <div className="rounded-lg border border-white/30 bg-white/50 backdrop-blur-sm p-4 mx-0 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="#FC4C02">
                      <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    {user?.stravaId ? (
                      <p className="text-xs text-green-700">
                        <span className="font-medium">Strava connected:</span>{" "}
                        Future activities will sync automatically. You can still
                        manually upload activities here if needed.
                      </p>
                    ) : (
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Connect with Strava</span>{" "}
                        to automatically sync activities.
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  {!user?.stravaId && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-blue-500 text-white hover:bg-blue-600 border-blue-400 h-6 px-2 text-xs"
                      onClick={handleStravaLink}
                      disabled={!user?.isVerified}
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>
              {!user?.isVerified && (
                <p className="text-xs text-amber-600 mt-2">
                  Please verify your email to connect Strava.
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <div className="flex flex-col gap-2 w-full">
            {step === "sport" ? (
              <div className="grid grid-cols-2 gap-2 w-full">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="border-white/50 bg-white/50 hover:bg-white/70"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => setStep("details")}
                  disabled={!selectedSport}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md"
                >
                  <ChevronRight className="mr-2 h-4 w-4" />
                  Next
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 w-full">
                <Button
                  variant="outline"
                  onClick={() => setStep("sport")}
                  className="border-white/50 bg-white/50 hover:bg-white/70"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!date || !duration || uploadActivity.isPending}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md"
                >
                  {uploadActivity.isPending ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Save Activity
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
